import React, { useState, useContext } from "react";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import { Carousel, ImageUploadModal } from "../../common/commonComps";
import { uploadImage } from "../../common/utils";
import { Context } from "../../common/context";
import { AccountModal } from "../account/accountPieces";
import type { Size, ContextValue, Config, CarouselItem } from "../../types";

const sizes: Record<Size, { h: string; w: string }> = {
    small: { h: `400px`, w: `95%` },
    medium: { h: `400px`, w: `95%` },
    large: { h: `400px`, w: `95%` },
};

const Wrap = styled(`div`)<{ size?: Size }>(({ size }) => ({
    maxWidth: size ? sizes[size]?.w : undefined,
    display: `block`,
    minWidth: `300px`,
    minHeight: `200px`,
    cursor: `pointer`,
}));

interface BigImageProps {
    size?: Size;
    editable: boolean;
}

const BigImage = ({ size, editable }: BigImageProps): React.ReactElement => {
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [fileLink, setFileLink] = useState<string | undefined>();
    const [file, setFile] = useState<File | undefined>();
    const [link, setLink] = useState<string>(``);

    const cx = useContext(Context) as ContextValue;
    const bigImage = cx.getValue
        ? (cx.getValue(`config`) as Config)?.bigImage
        : ({ text: `abc`, image: `http://cnn.com/logo` } as CarouselItem); // this is for testing because test-renderer dies on context

    const openModal = () => {
        setModalOpen(true);
        document.body.style.overflow = `hidden`;
    };

    const clearModal = () => {
        setLink(``);
        setFile(undefined);
        setFileLink(undefined);
        setModalOpen(false);
        document.body.style.overflow = `auto`;
    };

    const setImage = (e: React.ChangeEvent<HTMLInputElement>): void => {
        if (e.target.files) {
            const f = e.target.files[0];
            setFileLink(URL.createObjectURL(f));
            setFile(f);
        }
    };

    const sendImage = (): void => {
        if (!file) {
            setFile(undefined);
            setFileLink(undefined);
            setModalOpen(false);
            document.body.style.overflow = `auto`;
            cx.setContext({ bigImage: { ...bigImage, link: link || bigImage?.link } });
            setLink(``);
            return;
        }
        uploadImage(file).then(image => {
            setFile(undefined);
            setFileLink(undefined);
            setModalOpen(false);
            document.body.style.overflow = `auto`;
            cx.setContext({ bigImage: { ...bigImage, image, link: link || bigImage?.link } });
            setLink(``);
        });
    };

    return (
        <Wrap
            size={size}
            onClick={() => {
                if (!editable) {
                    if ((bigImage?.link || ``).includes(`http`)) {
                        window.open(bigImage?.link);
                    } else {
                        navigate(bigImage?.link ?? ``);
                    }
                }
            }}
        >
            <AccountModal open={modalOpen}>
                <ImageUploadModal
                    {...{ fileLink, context: bigImage, clearModal, link, setLink, sendImage, setImage }}
                />
            </AccountModal>
            <div
                style={{ minWidth: `300px`, minHeight: `200px` }}
                onClick={() => (editable ? openModal() : (() => {})())}
            >
                <Carousel src={bigImage?.image ?? ``} />
            </div>
        </Wrap>
    );
};

export default BigImage;
